import { TRAVEL_BOOKING_ADMIN_STATUS_LABELS, type TravelBookingAdminStatus } from '@utazzo/shared';

/** Paise (as the API's whole-number string) to "₹12,345.00". Tolerates junk. */
export function money(
  paise: string | bigint | number | null | undefined,
  currency = 'INR',
): string {
  if (paise === null || paise === undefined || paise === '') return '-';
  let minor: bigint;
  try {
    minor = typeof paise === 'bigint' ? paise : BigInt(String(paise));
  } catch {
    return '-';
  }
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const major = Number(abs) / 100;
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(major);
  return negative ? `-${formatted}` : formatted;
}

/** "+₹1,200" / "-₹300" / "no change" - the sign is the point. */
export function signedMoney(paise: string | null | undefined, currency = 'INR'): string {
  if (paise === null || paise === undefined) return '-';
  const n = BigInt(paise);
  if (n === 0n) return 'no change';
  return `${n > 0n ? '+' : ''}${money(n, currency)}`;
}

/** "6 Sep 2026" from an ISO date or timestamp. */
export function dateLabel(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "6 Sep 2026, 4:30 pm" from an ISO timestamp. */
export function dateTimeLabel(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "2h 15m ago" / "3d 4h ago" / "just now" since an instant. */
export function agoLabel(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '-';
  const diff = now - new Date(iso).getTime();
  if (!Number.isFinite(diff)) return '-';
  if (diff < 60_000) return 'just now';
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor((diff % 86_400_000) / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (d > 0) return `${d}d ${h}h ago`;
  if (h > 0) return `${h}h ${m}m ago`;
  return `${m}m ago`;
}

/** "in 1h 20m" / "overdue by 5m" against a deadline. */
export function untilLabel(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '-';
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  const span = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return diff >= 0 ? `in ${span}` : `overdue by ${span}`;
}

export function statusLabel(status: TravelBookingAdminStatus | string): string {
  return (
    TRAVEL_BOOKING_ADMIN_STATUS_LABELS[status as TravelBookingAdminStatus] ??
    status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
  );
}

/** The badge tone for a status. Amber is reserved for "someone must act". */
export function statusTone(
  status: TravelBookingAdminStatus | string,
): 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'CONFIRMED':
      return 'success';
    case 'ON_HOLD':
      return 'warning';
    case 'BOOK_FAILED':
      return 'destructive';
    case 'CANCELLED':
    case 'REFUNDED':
    case 'EXPIRED':
      return 'outline';
    case 'PAID':
    case 'BOOKING':
    case 'CANCELLATION_REQUESTED':
      return 'secondary';
    default:
      return 'default';
  }
}

/** Event kinds, as a person reads them. Unknown kinds fall back to the code. */
export function eventLabel(kind: string): string {
  const known: Record<string, string> = {
    PRICED: 'Priced',
    PAX_ENTERED: 'Traveller details saved',
    PAYMENT_ORDER: 'Payment order created',
    PAYMENT_CAPTURED: 'Payment captured',
    PAYMENT_RECOVERED: 'Payment recovered from gateway',
    ISSUE_STARTED: 'Sent to supplier',
    ADMIN_ISSUE_STARTED: 'Re-issue started by admin',
    ADMIN_ISSUE_ACCEPTED: 'Admin accepted the price difference',
    ADMIN_REQUOTE: 'Admin re-quoted',
    TBO_BOOK: 'Supplier: Book',
    TBO_TICKET: 'Supplier: Ticket',
    TBO_HOTEL_BOOK: 'Supplier: Book',
    TBO_HOTEL_BOOKING_DETAIL: 'Supplier: BookingDetail',
    TBO_BOOKING_DETAILS: 'Supplier: GetBookingDetails',
    TBO_RELEASE_PNR: 'Supplier: PNR released',
    CONFIRMED: 'Confirmed',
    BOOK_FAILED: 'Booking failed',
    ON_HOLD: 'Put on hold (supplier refused for our account)',
    HOLD_RELEASED: 'Hold released',
    REFUND_INITIATED: 'Refund initiated',
    REFUND_FAILED: 'Refund FAILED',
    REQUOTED: 'Re-quoted',
    REPRICED: 'Re-priced',
    EXPIRED: 'Expired',
    UNRESOLVED: 'Outcome unresolved after polling',
    DOCUMENTS_VIEWED: 'Document numbers viewed',
    CONFIRMATION_RESENT: 'Confirmation resent by admin',
    PAYMENT_AMOUNT_MISMATCH: 'Payment amount mismatch',
    PARTIAL_ISSUE_REFUND_HELD: 'Partly issued - refund held for a human',
    HOLD_REMINDER: 'Admins reminded (still on hold)',
    ADMIN_REFUND: 'Refund by admin',
    ADMIN_CANCEL_REQUESTED: 'Cancellation raised by admin',
    CANCELLED: 'Cancelled (supplier confirmed)',
    PARTIAL_CANCELLED: 'Partly cancelled (supplier confirmed)',
    CANCELLATION_REJECTED: 'Cancellation refused by supplier',
    CANCELLATION_REQUESTED: 'Cancellation sent to supplier',
  };
  return (
    known[kind] ??
    kind
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
  );
}
